import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, Slide } from "react-toastify";
import * as Auth from "../authService";
import * as notify from "../notify";
import {
  beginCognitoLogin,
  emailFromIdToken,
  exchangeCodeForTokens,
  fetchSsoConfig,
  rememberCognitoLogout,
  rememberSsoEmail,
  takeSsoAttempt,
} from "../sso/cognitoAuth";
import Logo from "../icons/logo.png";
import "../stylesheets/Login.css";

// Landing page for the Cognito redirect: code -> Cognito ID token -> Proview JWT.
const SsoCallback = () => {
  const navigate = useNavigate();
  const [message, setMessage] = useState("Completing sign-in…");
  // The authorization code and PKCE verifier are single use, so only ever run the exchange once
  // (React StrictMode runs effects twice in development).
  const started = useRef(false);

  useEffect(() => {
    if (started.current) {
      return;
    }
    started.current = true;

    const fail = (msg) => {
      setMessage(msg);
      notify.error(msg);
    };

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    const error = params.get("error");
    const errorDescription = params.get("error_description");

    // What the Login page asked for; read once so it never leaks into a later sign-in.
    const attempt = takeSsoAttempt();

    if (error) {
      fail(errorDescription || error);
      return;
    }
    if (!code || !state) {
      setMessage("Missing authorization code.");
      return;
    }

    (async () => {
      try {
        const config = await fetchSsoConfig();
        const tokens = await exchangeCodeForTokens(config, code, state);
        if (!tokens.id_token) {
          throw new Error("Cognito did not return an ID token");
        }

        // The login_hint sent to the IdP is only a hint: an existing Cognito or Microsoft session can still sign in
        // a different account. If the account that signed in is not the one typed, ask once for fresh credentials.
        const typedEmail = attempt && attempt.email ? attempt.email : "";
        const signedInEmail = emailFromIdToken(tokens.id_token);
        if (
          typedEmail &&
          signedInEmail &&
          typedEmail.toLowerCase() !== signedInEmail.toLowerCase()
        ) {
          if (!attempt.forced && attempt.identityProvider) {
            setMessage("Switching account…");
            await beginCognitoLogin(config, attempt.identityProvider, {
              email: typedEmail,
              forceLogin: true,
            });
            return;
          }
          fail(
            `You signed in as ${signedInEmail} but entered ${typedEmail}. Use the same email, or sign out of Microsoft and try again.`
          );
          return;
        }

        const res = await Auth.loginWithSso(tokens.id_token);
        if (!res || res.status !== 200) {
          fail(
            (res && res.data && res.data.message) ||
              "SSO login failed. Your account may not be provisioned in Proview."
          );
          return;
        }
        rememberCognitoLogout(config);
        // Offer "Continue as <email>" on the Login page next time. Only the address is kept, never a token.
        rememberSsoEmail(signedInEmail || typedEmail);
        navigate("/summary", { replace: true });
      } catch (e) {
        fail(e.message || "SSO login failed");
      }
    })();
  }, [navigate]);

  return (
    <div className="center">
      <div className="card">
        <div id="LoginLogo">
          <div className="logo-wrapper">
            <img src={Logo} alt="Login Logo" />
          </div>
        </div>
        <p className="sso-callback-message">{message}</p>
        <button
          className="proview-button login"
          type="button"
          onClick={() => navigate("/login", { replace: true })}
        >
          Back to Login
        </button>
      </div>
      <ToastContainer transition={Slide} />
    </div>
  );
};

export default SsoCallback;
